// Light/dark theme. Follows the system setting by default; a visitor's explicit choice
// is stored and applied as <html data-theme="light|dark">. The inline script in
// public/index.html applies a stored choice before first paint to avoid a flash.
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'sy-theme';
const THEME_COLORS = { dark: '#0A0A0A', light: '#FAF7F2' };
const systemQuery = () => window.matchMedia('(prefers-color-scheme: light)');

const readStored = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch (e) {
    return null; // storage blocked
  }
};

const systemTheme = () => (systemQuery().matches ? 'light' : 'dark');

export function useTheme() {
  const [stored, setStored] = useState(readStored);
  const [system, setSystem] = useState(systemTheme);
  const theme = stored || system;

  // Track system changes while the visitor hasn't picked a theme
  useEffect(() => {
    const query = systemQuery();
    const onChange = () => setSystem(systemTheme());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (stored) root.setAttribute('data-theme', stored);
    else root.removeAttribute('data-theme');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLORS[theme]);
  }, [stored, theme]);

  // Flip to the other theme. Choosing the same theme as the system clears the stored
  // choice, so the site goes back to following the system.
  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    const value = next === systemTheme() ? null : next;
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* storage blocked: the choice lasts for this page view */ }
    setStored(value);
  }, [theme]);

  return { theme, toggle };
}
